import {
    useState,
    useImperativeHandle,
    forwardRef,
    useCallback,
    useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { BaseModal } from "~/components";
import { fetchOrderStables } from "~/api/endpoints";
import TokenList from "~/components/TokenList/TokenList";
import { Placeholder } from "@telegram-apps/telegram-ui";
import { TokenColumn } from "../TokenList/TokenItem";
import { formatBalance, formatPrice, formatValue } from "~/utils/number-format";
import { TokenWithBalance } from "~/types";
import useSWR from "swr";
import { useUXUYWallet } from "~/Provider/UXUYWallet";
import { getMaximiumnPopupHeight } from "../widget/SafeAreaTop/SafeAreaTop";

interface ModalUTokenListProps {
    chainId?: number;
    contract?: string;
    onSelect: (item: TokenWithBalance) => void;
}

const ModalUTokenList = forwardRef((props: ModalUTokenListProps, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selectedChanId = props.chainId;
    const selectedContract = props.contract;

    const { getAddress, getAddressType } = useUXUYWallet();

    const { onSelect } = props;

    useImperativeHandle(ref, () => ({
        open: () => {
            setOpen(true);
        },
    }));

    const queryTokenList = useCallback((args: any) => {
        const [selectedChanId, selectedContract] = args;
        if (!selectedChanId) return null;
        const address = getAddress(selectedChanId) as string;
        const address_type = getAddressType(selectedChanId) as number;
        return fetchOrderStables(selectedChanId, address, address_type).then(
            (res) =>
                (res.data.list ?? []).filter(
                    (token) =>
                        token.token_base.contract.toLowerCase() !==
                        selectedContract.toLowerCase()
                )
        );
    }, []);

    const { data: tokenList, isLoading: tokenListLoading } = useSWR(
        [selectedChanId, selectedContract, "queryUTokenList"],
        queryTokenList
    );

    const handleItemClick = (item: any) => {
        onSelect(item);
        setOpen(false);
    };

    const tokenColumns: TokenColumn[] = useMemo(
        () => [
            {
                key: "price",
                label: `${t("price")} / ${t("changes")}`,
                value(token: any) {
                    return formatBalance(token.balance);
                },
                change(token: any) {
                    return formatValue(token.value);
                },
            },
        ],
        []
    );

    return (
        <BaseModal
            className="bg-bg_0 z-[999]"
            style={{
                height: getMaximiumnPopupHeight(),
            }}
            titleType="title"
            title={t("select_token")}
            open={open}
            modal={true}
            onclickHeaderClose={() => setOpen(false)}
            dismissible={true}
            onOpenChange={(bool) => setOpen(bool)}
        >
            <div
                className="flex relative w-full"
                style={{ height: "calc(95vh - 60px)" }}
            >
                <div className="w-full h-full bg-bg_0 flex flex-col">
                    {!tokenListLoading &&
                    tokenList &&
                    tokenList.length === 0 ? (
                        <Placeholder header={t("no_results")}>
                            <span className="text-[60px]">🤔</span>
                        </Placeholder>
                    ) : (
                        <>
                            <TokenList
                                isLoading={tokenListLoading}
                                tokens={tokenList as any}
                                columns={tokenColumns}
                                onTokenClick={handleItemClick}
                            />
                        </>
                    )}
                </div>
            </div>
        </BaseModal>
    );
});

export default ModalUTokenList;
